import { pick, uniq, uniqWith } from 'es-toolkit';

import { assertUnlessInProduction } from '@paragon/client-tools';
import {
  ComponentSide,
  DEFAULT_ORGANIZATION_SETTINGS,
  HeelReinforcementType,
  isFeaturePatternTruss,
  isProfileTruss,
  materialSettingsLumberProperties,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLumberLists, lumbersAreEqual } from '@paragon/material-lists';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { notNullish } from '@paragon/utilities';

import { updateFeatureAndFloatingJointsOnProfile } from '../../library/profiles/profileManipulation';
import { getDefaultGableSettings } from '../../library/profiles/profileTrussDefaults';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { createGetUniqueNameForComponentDesign } from '../selectors/uniqueComponentNameSelectors';
import {
  clearRewebbingInfo,
  RewebbingProfileSettings,
  setMemberAnalysisInfo,
  setWebbingInfo,
  startRewebbing,
} from '../slices/applicationSlice';
import { setComponent } from '../slices/componentDesignsSlice';
import { setTrussEditsPanel } from '../slices/panelDataSlice';
import { getLoadedStockLengthPricesForLumbers } from './insertComponentDesignActions';
import {
  __UNSAFE_loadProfileDefaultsUsingProjectGuidOrFallback,
  loadAnalysisSettings,
  loadLoadSettings,
  loadLumberPrices,
  loadMaterialSettingsAndPrices,
} from './loadActions';
import { loadOrganizationSettingsIfNeeded } from './settingsActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const setComponentName =
  (requestedName: string, componentDesignGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const componentDesign = state.componentDesigns.data[componentDesignGuid];
    if (componentDesign) {
      dispatch(
        setComponent({
          component: {
            ...componentDesign.component,
            name: createGetUniqueNameForComponentDesign(state)(requestedName, componentDesignGuid),
          },
          componentDesignGuid: componentDesign.guid,
        }),
      );
    }
  };

export const adjustComponentSpan =
  (adjustment: number, componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const oldComponentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!oldComponentDesign) return;

    const spinnerId = dispatch(addSpinnerMessage('Updating truss span...'));

    try {
      const updatedComponent = await designService.componentMacros.adjustSpan(
        oldComponentDesign.component,
        componentDesignGuid,
        adjustment,
      );
      dispatch(setComponent({ componentDesignGuid, component: updatedComponent }));
    } catch (err) {
      dispatch(enqueueError('There was an error updating the span.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const setComponentDepth =
  (newDepth: number, componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const oldComponentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!oldComponentDesign || !isFeaturePatternTruss(oldComponentDesign.component)) return;

    const spinnerId = dispatch(addSpinnerMessage('Updating truss depth...'));

    try {
      const updatedComponent = await designService.componentMacros.editDepth(oldComponentDesign.component, newDepth);
      dispatch(setComponent({ componentDesignGuid, component: updatedComponent }));
    } catch (err) {
      dispatch(enqueueError('There was an error updating the depth.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const setProfileTrussOverhangDistance =
  (side: ComponentSide, distance: number, componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const oldComponentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!oldComponentDesign || !isProfileTruss(oldComponentDesign.component)) return;

    const spinnerId = dispatch(addSpinnerMessage('Setting overhang distance...'));

    try {
      const updatedComponent = await designService.componentMacros.editOverhangDistance(
        oldComponentDesign.component,
        side,
        distance,
      );
      dispatch(setComponent({ componentDesignGuid, component: updatedComponent }));
    } catch (err) {
      dispatch(enqueueError('There was an error updating the overhang distance.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const setProfileTrussHeelReinforcement =
  (
    componentDesignGuid: string,
    heelReinforcementType: HeelReinforcementType,
    side: ComponentSide,
    distance: number | null,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const oldComponentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!oldComponentDesign || !isProfileTruss(oldComponentDesign.component)) return;

    const spinnerId = dispatch(addSpinnerMessage('Setting heel reinforcement...'));

    try {
      const updatedComponent = await designService.componentMacros.editHeelReinforcement(
        componentDesignGuid,
        oldComponentDesign.component,
        heelReinforcementType,
        side,
        distance,
      );
      dispatch(setComponent({ componentDesignGuid, component: updatedComponent }));
    } catch (err) {
      dispatch(enqueueError('There was an error updating the heel reinforcement.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const rewebTruss =
  (useWebbingInfo = false): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const activeComponentDesign = getActiveComponentDesign(getState());
    const activeComponent = activeComponentDesign?.component;
    const { rewebbingInfo } = getState().application;
    if (!activeComponentDesign || !isProfileTruss(activeComponent) || !rewebbingInfo) return;

    const lumberLists = getLumberLists(getState());
    if (!lumberLists) {
      dispatch(enqueueError('Failed to reweb truss. Material lists not loaded.'));
      return;
    }

    const {
      profileSettings,
      webbingInfo,
      loadSettings,
      analysisSettings,
      materialSettings,
      jackConnections,
      jackInformation,
    } = rewebbingInfo;
    const { profile, gableSettings } = profileSettings;

    if (useWebbingInfo && !webbingInfo) {
      assertUnlessInProduction('No webbing info found.');
      return;
    }
    const specifiedWebbingInfo = useWebbingInfo && webbingInfo ? webbingInfo : null;

    const lumbers = uniqWith(
      Object.values(pick(materialSettings, materialSettingsLumberProperties)).concat(
        activeComponentDesign.component.members.map((member) => member.lumber),
      ),
      lumbersAreEqual,
    );
    await dispatch(loadLumberPrices(lumbers));
    const stockLengthPrices = await getLoadedStockLengthPricesForLumbers(getState(), lumbers);

    const organizationSettings = await dispatch(loadOrganizationSettingsIfNeeded());
    const { scarfCutThreshold } = organizationSettings ?? DEFAULT_ORGANIZATION_SETTINGS;

    const spinnerId = dispatch(addSpinnerMessage('Re-webbing truss...'));

    try {
      const {
        truss: rewebbedTruss,
        webbingInfo,
        memberAnalysisInfo,
      } = await designService.componentMacros.rewebTruss(
        activeComponent,
        profile,
        gableSettings,
        lumberLists[activeComponentDesign.materialSelection.lumberListGuid],
        loadSettings,
        activeComponentDesign.additionalLoads,
        jackConnections,
        jackInformation,
        analysisSettings,
        materialSettings,
        scarfCutThreshold,
        activeComponentDesign.lockedMembers,
        stockLengthPrices,
        specifiedWebbingInfo,
      );
      await dispatch(loadLumberPrices(rewebbedTruss.members.map((member) => member.lumber)));
      dispatch(setComponent({ componentDesignGuid: activeComponentDesign.guid, component: rewebbedTruss }));
      dispatch(setWebbingInfo(webbingInfo));
      dispatch(setMemberAnalysisInfo(memberAnalysisInfo));
    } catch (err) {
      console.error(err);
      dispatch(enqueueError('There was an error re-webbing the truss.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const startRewebTruss = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  const activeComponentDesign = getActiveComponentDesign(state);
  const activeComponent = activeComponentDesign?.component;
  if (!activeComponentDesign || !isProfileTruss(activeComponent)) return;

  const jackConnections = Object.values(state.jackGirderConnections.data).filter(
    (connection) => connection.girderGuid === activeComponentDesign.guid,
  );
  const jackGuids = uniq(jackConnections.map((connection) => connection.jackGuid));

  const spinnerId = dispatch(addSpinnerMessage('Getting rewebbing info...'));

  let success = false;
  try {
    const [rewebbingStartInfo, loadSettings, analysisSettings, materialSettings, profileDefaults, jackInformation] =
      await Promise.all([
        designService.componentMacros.startRewebTruss(activeComponent),
        dispatch(loadLoadSettings(activeComponentDesign.guid)),
        dispatch(loadAnalysisSettings(activeComponentDesign.guid)),
        dispatch(loadMaterialSettingsAndPrices(activeComponentDesign.guid)),
        dispatch(__UNSAFE_loadProfileDefaultsUsingProjectGuidOrFallback()),
        designService.componentDesigns.postGetJackInformation(jackGuids),
      ]);

    if (!materialSettings.profileMaterialSettings) throw new Error('Expected profile truss settings to exist.');

    const profileSettings: RewebbingProfileSettings = {
      profile: updateFeatureAndFloatingJointsOnProfile(rewebbingStartInfo),
      gableSettings: getDefaultGableSettings(profileDefaults),
    };

    // If the truss doesn't have jack information, just remove it for now
    const jackGuidsWithJackInformation = new Set(jackGuids.filter((jackGuid) => jackInformation[jackGuid]));
    const jackConnectionsWithJackInformation = jackConnections.filter((connection) =>
      jackGuidsWithJackInformation.has(connection.jackGuid),
    );
    const nonNullJackInformation = Object.fromEntries(
      Object.entries(jackInformation)
        .map(([jackGuid, jackInformation]) => (jackInformation ? [jackGuid, jackInformation] : null))
        .filter(notNullish),
    );

    dispatch(
      startRewebbing({
        profileSettings,
        loadSettings,
        analysisSettings,
        materialSettings: materialSettings.profileMaterialSettings,
        jackConnections: jackConnectionsWithJackInformation,
        jackInformation: nonNullJackInformation,
      }),
    );
    dispatch(setTrussEditsPanel('reweb-profile'));
    success = true;
  } catch (err) {
    console.error(err);
    dispatch(enqueueError('There was an error re-webbing the truss.'));

    dispatch(clearRewebbingInfo());
    dispatch(setTrussEditsPanel(null));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
  if (success) {
    dispatch(rewebTruss());
  }
};
